import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { OrgComponentProps, OrgSettingsTabType, OrgSettingsTypeComponentMatchParams } from '../../typescriptHelpers';
import { antGrey, pageContentStyle, runwayColors, titleStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Layout, { Content } from 'antd/lib/layout/layout';
import { Event } from '../../utils/analytics';
import Title from 'antd/lib/typography/Title';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Button from 'antd/lib/button';
import {
    LockOutlined,
    TeamOutlined,
    CloudOutlined,
    ApiOutlined,
    AppstoreAddOutlined,
    ApartmentOutlined,
    TagsOutlined,
    MobileOutlined,
    CodeOutlined,
} from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import {
    selectOrg,
    selectIsStatelessServer,
    selectUserIsRunwayAdmin,
    selectOrgSubscriptionPlanText,
    selectIsBuildDistroEnabledForOrg,
} from '../../selectors/selectors';
import ChangeOrgModal from '../userSettings/ChangeOrgModal';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { trackOrgRouteEvent } from '../../actions/trackEvent';
import Tabs from 'antd/lib/tabs';
import WebhooksSettings from '../webhooks/WebhooksSettings';
import OrgSettingsTeam from './OrgSettingsTeam';
import { createOrgSettingsRoute } from '../../helpers/routeHelpers';
import ApiSettings from './ApiSettings';
import { SsoSamlSettings } from './SsoSamlSettings';
import AISettings from './AISettings';
import { IntegrationProviderSettings } from './IntegrationProviderSettings';
import { globalMessage } from '../../utils/globalMessage';
import { useUpdated } from '@/utils/hooks/helpers';
import { OrgChecklistItemTemplates } from './OrgChecklistItems';
import OrgTagsSettings from './OrgTagsSettings';
import BuildDistroSettings from './BuildDistroSettings';
import { ConfigAsCodeHistory } from './ConfigAsCodeHistory';

const AISettingsTabIcon: React.FC<{ color: string }> = ({ color }) => {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginRight: 8, flexShrink: 0 }}
        >
            <path
                d="M7.04614 4.21814C7.2483 4.74879 7.6669 5.16731 8.19653 5.37146V5.37244L8.20337 5.37537L9.43188 5.84314L8.20337 6.31091C7.67139 6.51368 7.25059 6.93365 7.04712 7.46521L7.04614 7.46814L6.57837 8.69666L6.1106 7.46814L6.10571 7.4574C5.90112 6.92974 5.48244 6.51269 4.95337 6.31091L3.72388 5.84314L4.95337 5.37537C5.48016 5.17466 5.89702 4.76091 6.10278 4.23669H6.10376L6.10669 4.22693L6.1106 4.21814L6.57837 2.98865L7.04614 4.21814Z"
                stroke={color}
                strokeWidth="1.5"
            />
            <path
                d="M4.26413 13.4457C4.37199 13.8055 4.65414 14.0869 5.01413 14.1947L5.28268 14.2748L5.01413 14.3558C4.69926 14.4502 4.44407 14.6776 4.31296 14.974L4.26413 15.1058L4.18307 15.3744L4.103 15.1058L4.05515 14.974C3.92406 14.6775 3.6688 14.4502 3.35397 14.3558L3.08444 14.2748L3.35397 14.1947C3.71374 14.0868 3.99512 13.8055 4.103 13.4457L4.18307 13.1761L4.26413 13.4457Z"
                stroke={color}
                strokeWidth="1.5"
            />
            <path
                d="M13.7965 9.95898L13.8014 9.9707V9.97168L13.8023 9.9707C14.0175 10.5267 14.4289 10.9805 14.9547 11.249L15.1881 11.3535H15.19L15.1939 11.3555L17.4352 12.209L15.2008 13.0605H15.1998L15.1949 13.0615L15.191 13.0635C14.5531 13.3071 14.049 13.8099 13.8023 14.4463H13.8014V14.4473L13.7965 14.459L13.7955 14.4619L12.943 16.7012L12.0904 14.4619L12.0885 14.458C11.8434 13.8157 11.3363 13.3085 10.6939 13.0635L10.693 13.0625H10.692C10.6906 13.062 10.6889 13.0612 10.6871 13.0605H10.6861L8.4498 12.209L10.6871 11.3574C10.691 11.356 10.6946 11.3544 10.6969 11.3535H10.6978L10.9312 11.249C11.3844 11.0176 11.753 10.6488 11.984 10.1953L12.0885 9.96191L12.0914 9.95312L12.0904 9.95215L12.942 7.71582L13.7965 9.95898Z"
                stroke={color}
                strokeWidth="1.5"
            />
        </svg>
    );
};

const OrgSettings: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const history = useHistory();
    const params = useParams<OrgSettingsTypeComponentMatchParams>();
    const [changeOrgModalVisible, setChangeOrgModalVisible] = React.useState(false);

    const dispatch = useDispatch();

    const currentSubscriptionPlanText = useSelector((state: RootState) =>
        selectOrgSubscriptionPlanText(state, props.orgId),
    );
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const isStatelessServer = useSelector((state: RootState) => selectIsStatelessServer(state));
    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));

    const settingUpdated = useUpdated(OrgSettingsUpdatable.MergeAccountsSetting);

    const showChangeOrg = isStatelessServer && isRunwayAdmin;
    const trackedOrgSettingsViewOrgIdRef = React.useRef<string>(); // this ref will help us avoid event dupes

    const showBuildDistroSettings = useSelector((state: RootState) => selectIsBuildDistroEnabledForOrg(state));

    React.useEffect(() => {
        if (settingUpdated) {
            globalMessage.success('Organization setting updated!');
        }
    }, [settingUpdated]);

    const activeTab = params.orgSettingsTabType ?? OrgSettingsTabType.Team;

    // track amplitude
    React.useEffect(() => {
        if (activeTab === OrgSettingsTabType.Checklists) {
            dispatch(trackOrgRouteEvent({ orgId: props.orgId, eventName: Event.ViewOrgChecklistItems }));
            return;
        }

        if (activeTab === OrgSettingsTabType.Tags) {
            dispatch(
                trackOrgRouteEvent({
                    orgId: props.orgId,
                    eventName: Event.ViewTagManagement,
                    viewType: 'orgSettings',
                }),
            );
            return;
        }

        if (trackedOrgSettingsViewOrgIdRef.current === props.orgId) {
            return;
        }

        trackedOrgSettingsViewOrgIdRef.current = props.orgId;
        dispatch(trackOrgRouteEvent({ orgId: props.orgId, eventName: Event.ViewOrgSettings }));
    }, [activeTab, dispatch, props.orgId]);

    return (
        <Layout
            style={{
                ...pageContentStyle,
                position: 'relative',
                paddingLeft: 32,
                paddingRight: 32,
                paddingBottom: 0,
            }}
        >
            <Space direction="vertical" style={{ marginBottom: 24 }}>
                <Space align="center">
                    <Title style={{ fontSize: 24, marginBottom: 0 }}>{org?.name}</Title>
                    {currentSubscriptionPlanText && (
                        <Tag
                            style={{
                                borderRadius: 30,
                                fontWeight: 600,
                                fontSize: 11,
                                borderStyle: 'none',
                                paddingLeft: 8,
                                paddingRight: 8,
                                paddingTop: 2,
                                paddingBottom: 2,
                                color: runwayColors.white,
                            }}
                            color={antGrey.grey8}
                        >
                            {currentSubscriptionPlanText}
                        </Tag>
                    )}
                    {showChangeOrg && (
                        <Button type="link" onClick={() => setChangeOrgModalVisible(true)}>
                            Switch organizations
                        </Button>
                    )}
                </Space>
                <Text style={titleStyle}>Organization settings</Text>
            </Space>
            <Tabs
                animated={false}
                activeKey={activeTab}
                onChange={(activeKey) => {
                    const tabType = activeKey as OrgSettingsTabType;
                    const route = createOrgSettingsRoute(params.orgIdParam, tabType);

                    history.push(route);
                }}
                style={{ height: '100%' }}
                className="org-settings-tabs"
            >
                <Tabs.TabPane
                    tab={
                        <Text
                            style={{
                                color:
                                    activeTab === OrgSettingsTabType.Team ?
                                        runwayColors.primary
                                    :   runwayColors.darkTextGrey,
                            }}
                        >
                            Team
                        </Text>
                    }
                    icon={
                        <TeamOutlined
                            style={{
                                color: activeTab === OrgSettingsTabType.Team ? runwayColors.primary : antGrey.grey6,
                            }}
                        />
                    }
                    key="team"
                >
                    <OrgSettingsTeam orgId={props.orgId} />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Text
                            style={{
                                color:
                                    activeTab === OrgSettingsTabType.Checklists ?
                                        runwayColors.primary
                                    :   runwayColors.darkTextGrey,
                            }}
                        >
                            Checklist items
                        </Text>
                    }
                    icon={
                        <ApartmentOutlined
                            style={{
                                color:
                                    activeTab === OrgSettingsTabType.Checklists ? runwayColors.primary : antGrey.grey6,
                            }}
                        />
                    }
                    key={OrgSettingsTabType.Checklists}
                >
                    <Content>
                        <OrgChecklistItemTemplates orgId={props.orgId} />
                    </Content>
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Text
                            style={{
                                color:
                                    activeTab === OrgSettingsTabType.Webhooks ?
                                        runwayColors.primary
                                    :   runwayColors.darkTextGrey,
                            }}
                        >
                            Webhooks
                        </Text>
                    }
                    icon={
                        <CloudOutlined
                            style={{
                                color: activeTab === OrgSettingsTabType.Webhooks ? runwayColors.primary : antGrey.grey6,
                            }}
                        />
                    }
                    key={OrgSettingsTabType.Webhooks}
                >
                    <Content>
                        <WebhooksSettings orgId={props.orgId} />
                    </Content>
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Text
                            style={{
                                color:
                                    activeTab === OrgSettingsTabType.Api ?
                                        runwayColors.primary
                                    :   runwayColors.darkTextGrey,
                            }}
                        >
                            API
                        </Text>
                    }
                    icon={
                        <ApiOutlined
                            style={{
                                color: activeTab === OrgSettingsTabType.Api ? runwayColors.primary : antGrey.grey6,
                            }}
                        />
                    }
                    key="api"
                >
                    <ApiSettings orgId={props.orgId} />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Text
                            style={{
                                color:
                                    activeTab === OrgSettingsTabType.SsoSaml ?
                                        runwayColors.primary
                                    :   runwayColors.darkTextGrey,
                            }}
                        >
                            SSO/SAML
                        </Text>
                    }
                    icon={
                        <LockOutlined
                            style={{
                                color: activeTab === OrgSettingsTabType.SsoSaml ? runwayColors.primary : antGrey.grey6,
                            }}
                        />
                    }
                    key="sso-saml"
                >
                    <SsoSamlSettings orgId={props.orgId} />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Text
                            style={{
                                color:
                                    activeTab === OrgSettingsTabType.Tags ?
                                        runwayColors.primary
                                    :   runwayColors.darkTextGrey,
                            }}
                        >
                            Tags
                        </Text>
                    }
                    icon={
                        <TagsOutlined
                            style={{
                                color: activeTab === OrgSettingsTabType.Tags ? runwayColors.primary : antGrey.grey6,
                            }}
                        />
                    }
                    key={OrgSettingsTabType.Tags}
                >
                    <OrgTagsSettings orgId={props.orgId} />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Text
                            style={{
                                color:
                                    activeTab === OrgSettingsTabType.AI ?
                                        runwayColors.primary
                                    :   runwayColors.darkTextGrey,
                            }}
                        >
                            AI
                        </Text>
                    }
                    icon={
                        <AISettingsTabIcon
                            color={activeTab === OrgSettingsTabType.AI ? runwayColors.primary : antGrey.grey6}
                        />
                    }
                    key={OrgSettingsTabType.AI}
                >
                    <AISettings orgId={props.orgId} />
                </Tabs.TabPane>
                <Tabs.TabPane
                    tab={
                        <Text
                            style={{
                                color:
                                    activeTab === OrgSettingsTabType.Integration ?
                                        runwayColors.primary
                                    :   runwayColors.darkTextGrey,
                            }}
                        >
                            Integrations
                        </Text>
                    }
                    icon={
                        <AppstoreAddOutlined
                            style={{
                                color:
                                    activeTab === OrgSettingsTabType.Integration ? runwayColors.primary : antGrey.grey6,
                            }}
                        />
                    }
                    key="integration"
                >
                    <IntegrationProviderSettings orgId={props.orgId} />
                </Tabs.TabPane>
                {showBuildDistroSettings ?
                    <Tabs.TabPane
                        tab={
                            <Text
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.BuildDistro ?
                                            runwayColors.primary
                                        :   runwayColors.darkTextGrey,
                                }}
                            >
                                Build distro
                            </Text>
                        }
                        icon={
                            <MobileOutlined
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.BuildDistro ?
                                            runwayColors.primary
                                        :   antGrey.grey6,
                                }}
                            />
                        }
                        key={OrgSettingsTabType.BuildDistro}
                    >
                        <BuildDistroSettings orgId={props.orgId} />
                    </Tabs.TabPane>
                :   null}
                {org?.enableConfigAsCodeV2 ?
                    <Tabs.TabPane
                        tab={
                            <Text
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.ConfigAsCode ?
                                            runwayColors.primary
                                        :   runwayColors.darkTextGrey,
                                }}
                            >
                                Configuration as code
                            </Text>
                        }
                        icon={
                            <CodeOutlined
                                style={{
                                    color:
                                        activeTab === OrgSettingsTabType.ConfigAsCode ?
                                            runwayColors.primary
                                        :   antGrey.grey6,
                                }}
                            />
                        }
                        key={OrgSettingsTabType.ConfigAsCode}
                    >
                        <ConfigAsCodeHistory orgId={props.orgId} />
                    </Tabs.TabPane>
                :   null}
            </Tabs>
            {showChangeOrg && (
                <ChangeOrgModal
                    open={changeOrgModalVisible}
                    currentOrgId={props.orgId}
                    onCancel={() => setChangeOrgModalVisible(false)}
                    onSuccess={() => setChangeOrgModalVisible(false)}
                />
            )}
        </Layout>
    );
};

export default OrgSettings;
